import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import AccountExportOptions from "../../AccountExportOptions";
import TableSkeleton from "../../TableSkeleton";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";

const API_URL = "https://api.muktihospital.com/api/pathology-accounts/account-summary";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

// Contains last 3 months of raw bills + raw agent WITHDRAWAL transactions,
// so we can rebuild Account Summary fully client-side when offline.
const BULK_CACHE_KEY = "pathology_account_summary_bulk_recent";

/**
 * Given a date range and a raw bulk payload (bills + withdrawals), filter
 * both arrays to that range and compute the same summary shape the backend
 * /account-summary endpoint returns. Used only as an offline fallback.
 *
 * @param {{start: Date, end: Date}} range  inclusive date window
 * @param {{bills: any[], withdrawals: any[]}} bulk  payload from BULK_CACHE_KEY
 * @returns {{bills: any[], summary: object}}
 */
const computeSummaryFromBulk = (range, bulk) => {
    const startMs = range.start.getTime();
    const endMs = range.end.getTime();
    const inRange = (iso) => {
        if (!iso) return false;
        const t = new Date(iso).getTime();
        return t >= startMs && t <= endMs;
    };

    const bills = (bulk?.bills || []).filter((b) => inRange(b.createdAt));

    const totalBusiness = bills.reduce(
        (s, b) => s + (Number(b.netAmount) || 0), 0
    );
    const totalCollected = bills.reduce(
        (s, b) => s + (Number(b.paymentAmount) || 0), 0
    );
    const totalDue = bills.reduce(
        (s, b) => s + (Number(b.dueAmount) || 0), 0
    );

    // Mirror the online behavior: doctor expense = actually-paid commission,
    // taken from the per-bill doctorCommissionPaid the bulk cache stores.
    let totalDoctorPaid = 0;
    for (const b of bills) {
        totalDoctorPaid += Number(b.doctorCommissionPaid) || 0;
    }

    const totalAgentCommission = (bulk?.withdrawals || [])
        .filter((w) => inRange(w.createdAt))
        .reduce((s, w) => s + (Number(w.amount) || 0), 0);

    let discountHospital = 0;
    let discountDoctor = 0;
    let discountAgent = 0;

    bills.forEach(b => {
        const amt = Number(b.discountAmount || b.discount || 0);
        if (b.discountSource === 'doctor') discountDoctor += amt;
        else if (b.discountSource === 'agent') discountAgent += amt;
        else discountHospital += amt;
    });

    const netProfit = totalCollected - totalDoctorPaid - totalAgentCommission;

    return {
        bills,
        summary: {
            totalBills: bills.length,
            totalBusiness,
            totalCollected,
            totalDue,
            totalDoctorPaid,
            totalAgentCommission,
            netProfit,
            discountHospital,
            discountDoctor,
            discountAgent
        },
    };
};

/**
 * Convert a filter params object (the same one used to build the request to
 * /account-summary) into a concrete {start, end} Date range.
 */
const paramsToRange = (params) => {
    if (params.type === "daily") {
        return {
            start: new Date(new Date(params.startDate).setHours(0, 0, 0, 0)),
            end: new Date(new Date(params.endDate).setHours(23, 59, 59, 999)),
        };
    }
    if (params.type === "monthly") {
        const y = parseInt(params.year, 10);
        const m = parseInt(params.month, 10);
        return {
            start: new Date(y, m - 1, 1, 0, 0, 0, 0),
            end: new Date(y, m, 0, 23, 59, 59, 999),
        };
    }
    // yearly
    const y = parseInt(params.year, 10);
    return {
        start: new Date(y, 0, 1, 0, 0, 0, 0),
        end: new Date(y, 11, 31, 23, 59, 59, 999),
    };
};

/**
 * Returns true if the requested date range falls entirely inside the bulk
 * cache window. If even one day spills outside, we can't safely serve from
 * the bulk fallback (numbers would be partial / misleading).
 */
const isRangeInsideBulk = (range, bulk) => {
    if (!bulk?.windowStart || !bulk?.windowEnd) return false;
    const ws = new Date(bulk.windowStart).getTime();
    const we = new Date(bulk.windowEnd).getTime();
    return range.start.getTime() <= we && range.end.getTime() >= ws;
};

const PathologyAccountSummary = () => {
    const [loading, setLoading] = useState(false);
    const [bills, setBills] = useState([]);
    const [summary, setSummary] = useState({ 
        totalCollected: 0, 
        totalDoctorPaid: 0, 
        totalAgentCommission: 0, 
        netProfit: 0, 
        totalBills: 0,
        discountHospital: 0,
        discountDoctor: 0,
        discountAgent: 0
    });
    const [performerPayouts, setPerformerPayouts] = useState([]);
    const [otherExpenses, setOtherExpenses] = useState([]);

    // Filters
    const [filterType, setFilterType] = useState("monthly");
    const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [bulkOfflineActive, setBulkOfflineActive] = useState(false);
    const itemsPerPage = 20;

    const filterOptions = [
        { value: "daily", label: "Daily" },
        { value: "monthly", label: "Monthly" },
        { value: "yearly", label: "Yearly" },
    ];

    const fetchSummary = useCallback(async () => {
        setLoading(true);
        const params = { type: filterType };
        if (filterType === "daily") { params.startDate = startDate; params.endDate = endDate; }
        else if (filterType === "monthly") { const [y, m] = selectedMonth.split("-"); params.year = y; params.month = m; }
        else if (filterType === "yearly") { params.year = selectedYear; }

        // One cache slot per filter combination so different views stay separate
        // when the user is offline.
        const cacheKey = `pathology_account_summary_${JSON.stringify(params)}`;

        try {
            const response = await axios.get(API_URL, {
                headers: { "x-api-key": API_KEY },
                params: params,
            });

            const payload = {
                bills: response.data.bills || [],
                summary: response.data.summary,
                performerPayouts: response.data.performerPayouts || [],
                otherExpenses: response.data.otherExpenses || [],
            };
            setBills(payload.bills);
            setSummary(payload.summary);
            setPerformerPayouts(payload.performerPayouts);
            setOtherExpenses(payload.otherExpenses);
            setBulkOfflineActive(false);
            saveToCache(cacheKey, payload);
        } catch (err) {
            // Offline / server down: try cached data for this filter combination.
            if (err.code === "ERR_NETWORK" || !navigator.onLine) {
                // 1. Best case: the user already opened this exact filter
                //    online before, so we have a ready-made summary cached.
                const cached = await readFromCache(cacheKey);
                if (cached && (cached.bills || cached.summary)) {
                    setBills(cached.bills || []);
                    setSummary(cached.summary);
                    setPerformerPayouts(cached.performerPayouts || []);
                    setOtherExpenses(cached.otherExpenses || []);
                    setBulkOfflineActive(false);
                    toast.info("Offline: showing cached summary.");
                } else {
                    const bulk = await readFromCache(BULK_CACHE_KEY);
                    const range = paramsToRange(params);
                    if (bulk && isRangeInsideBulk(range, bulk)) {
                        const computed = computeSummaryFromBulk(range, bulk);
                        setBills(computed.bills);
                        setSummary(computed.summary);
                        setPerformerPayouts([]);
                        setOtherExpenses([]);
                        setBulkOfflineActive(true);
                        toast.info("Offline: showing computed summary from last 3 months.");
                    } else {
                        // 3. Out of luck: filter is outside the cached window
                        setBills([]);
                        setSummary(null);
                        setBulkOfflineActive(false);
                        toast.info("Offline: only last 3 months of data is available. Please connect to internet for older records.");
                    }
                }
            } else {
                toast.error("Failed to load account summary");
            }
        } finally {
            setLoading(false);
        }
    }, [filterType, startDate, endDate, selectedMonth, selectedYear]);

    useEffect(() => { fetchSummary(); }, [fetchSummary]);

    const { incomeData, expenseData } = useMemo(() => {
    const income = [];
    const expense = [];

    bills.forEach(bill => {
        income.push({
            date: bill.createdAt,
            description: `Pathology Bill: ${bill.patientName} (${bill.billNumber})`,
            net: Number(bill.netAmount) || 0,
            paid: Number(bill.paymentAmount) || 0,
            due: Number(bill.dueAmount) || 0
        });

       if (bill.commissionPaid > 0 && bill.status === "Complete") { 
            expense.push({
                date: bill.createdAt,
                description: `Agent Comm: ${bill.refer || 'Agent'} (${bill.billNumber})`,
                amount: Number(bill.commissionPaid) || 0
            });
        }

        // Doctor fee expense now mirrors the agent expense above: it only
        // appears once the doctor's commission has ACTUALLY been paid out
        // (bill.doctorCommissionPaid from the doctor ledger), not when it was
        // merely assigned at bill creation.
        if (bill.doctorCommissionPaid > 0 && bill.status === "Complete") {
            let docName = "Doctor";
            const firstDocPay = (bill.payments || []).find(p => p.doctor?.translations);
            if (firstDocPay?.doctor?.translations) {
                try {
                    const t = typeof firstDocPay.doctor.translations === 'string' ? JSON.parse(firstDocPay.doctor.translations) : firstDocPay.doctor.translations;
                    docName = t?.en?.name || t?.bn?.name || "Doctor";
                } catch (e) { docName = "Doctor"; }
            }
            expense.push({
                date: bill.createdAt,
                description: `Dr. Fee: ${docName} (${bill.billNumber})`,
                amount: Number(bill.doctorCommissionPaid) || 0
            });
        }
    });

    // Performer (doctor/technician test) payouts — standalone cash-outs, shown
    // as date-based expense rows so the table total matches the cards.
    (performerPayouts || []).forEach(po => {
        expense.push({
            date: po.createdAt,
            description: `Performer Payout: ${po.performerName || 'Performer'}${po.note ? ' — ' + po.note : ''}`,
            amount: Number(po.amount) || 0
        });
    });

    // Others Expense (reagent/repair/utility/other) — staff + general.
    (otherExpenses || []).forEach(ex => {
        expense.push({
            date: ex.expenseDate,
            description: `Others: ${ex.category || 'Expense'}${ex.userName ? ' — ' + ex.userName : ' — General'}${ex.note ? ' (' + ex.note + ')' : ''}`,
            amount: Number(ex.amount) || 0
        });
    });

    return { 
        incomeData: income.sort((a, b) => new Date(b.date) - new Date(a.date)), 
        expenseData: expense.sort((a, b) => new Date(b.date) - new Date(a.date)) 
    };
}, [bills, performerPayouts, otherExpenses]);

    const formatDateDisplay = (date) => new Date(date).toLocaleDateString("en-GB");

    const combinedExportData = useMemo(() => {
    return [
        ...incomeData.map(item => ({ ...item, type: 'INCOME' })),
        ...expenseData.map(item => ({ ...item, type: 'EXPENSE' }))
    ];
}, [incomeData, expenseData]);

    // Export Configuration
    const exportHeaders = ["Date", "Description", "Type", "Net/Amount", "Paid", "Due"];
    const combinedMapper = (row) => {
    const date = formatDateDisplay(row.date);
    const desc = row.description || "";
    const type = row.type || "N/A";
    
    // If it's an income row, show Net, Paid, Due
    if (row.type === 'INCOME') {
        return [
            date,
            desc,
            type,
            (row.net || 0).toFixed(2),
            (row.paid || 0).toFixed(2),
            (row.due || 0).toFixed(2)
        ].join("\t");
    } 
    
    return [
        date,
        desc,
        type,
        (row.amount || 0).toFixed(2),
        "0.00",
        "0.00"
    ].join("\t");
};
    
    const exportSubTitle = `Total Collected: BDT ${(summary?.totalCollected || 0).toFixed(2)} | Hospital Disc: BDT ${(summary?.discountHospital || 0).toFixed(2)} | Doc Disc: BDT ${(summary?.discountDoctor || 0).toFixed(2)} | Net Profit: BDT ${(summary?.netProfit || 0).toFixed(2)}`;

    // --- PAGINATION LOGIC ---
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Calculate total pages based on the longer list
    const totalPages = useMemo(() => {
        const maxItems = Math.max(incomeData.length, expenseData.length);
        return Math.ceil(maxItems / itemsPerPage) || 1;
    }, [incomeData.length, expenseData.length, itemsPerPage]);

    // Slice data for the current page
    const paginatedIncome = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return incomeData.slice(start, start + itemsPerPage);
    }, [incomeData, currentPage, itemsPerPage]);

    const paginatedExpense = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return expenseData.slice(start, start + itemsPerPage);
    }, [expenseData, currentPage, itemsPerPage]);

    const renderPagination = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(
                <li key={i}>
                    <button
                        onClick={() => handlePageChange(i)}
                        className={`table-pagination-item ${currentPage === i ? "bg-M-primary-color text-white" : ""}`}
                    >
                        {i}
                    </button>
                </li>
            );
        }
        return pages;
    };

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filterType, startDate, endDate, selectedMonth, selectedYear]);

    return (
        <div>
            <ToastContainer position="top-right" className="print-hidden" />
            <div className="print-hidden">
                <PageHeading title="Pathology Account Summary" breadcrumbs={[{ label: "Dashboard", url: "/" }, { label: "Pathology" }, { label: "Summary" }]} />
            </div>

            {bulkOfflineActive && (
                <div className="print-hidden mb-3 rounded-md border border-orange-200 bg-orange-50 px-4 py-2.5 text-sm text-orange-700 flex items-center gap-2">
                    <span className="font-semibold">⚠ Offline mode</span>
                    <span>— Showing data from last 3 months only. Connect to internet for older records.</span>
                </div>
            )}

            <div className="bg-white shadow-sm rounded-lg">
                {/* Filter Header */}
                <div className="border-b border-dashed border-gray-300 p-4 flex items-center justify-between gap-2 flex-wrap print-hidden">
                    <h2 className="text-base font-medium text-gray-700">Financial Overview</h2>
                    <div className="flex items-center gap-2">
                        <DynamicSelect options={filterOptions} value={filterOptions.find(o => o.value === filterType)} onChange={(o) => setFilterType(o.value)} isSearchable={false} />
                        {filterType === "daily" && <div className="flex gap-2"><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border p-2 text-sm rounded-md" /><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border p-2 text-sm rounded-md" /></div>}
                        {filterType === "monthly" && <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="border p-2 text-sm rounded-md" />}
                        {filterType === "yearly" && <input type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="border p-2 text-sm rounded-md w-24" />}
                        
                        <AccountExportOptions 
                            data={combinedExportData}
                            title="Pathology Full Account Summary" 
                            subTitle={exportSubTitle} 
                            headers={exportHeaders} 
                            dataMapper={combinedMapper} 
                            dropdownOpen={dropdownOpen} 
                            toggleDropdown={() => setDropdownOpen(!dropdownOpen)} 
                        />
                    </div>
                </div>

                <div className="p-4">
                    {/* --- Summary Cards --- */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg border-l-4 border-blue-500 shadow-sm border">
                            <p className="text-gray-500 text-[10px] font-bold uppercase">Total Bills</p>
                            <p className="text-lg font-black">{summary?.totalBills || 0}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-indigo-500 shadow-sm border">
                            <p className="text-gray-500 text-[10px] font-bold uppercase">Net Business</p>
                            <p className="text-lg font-black text-indigo-600">৳ {(summary?.totalBusiness || 0).toLocaleString()}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-green-500 shadow-sm border">
                            <p className="text-gray-500 text-[10px] font-bold uppercase">Collected</p>
                            <p className="text-lg font-black text-green-600">৳ {(summary?.totalCollected || 0).toLocaleString()}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-red-500 shadow-sm border">
                            <p className="text-gray-500 text-[10px] font-bold uppercase">Total Due</p>
                            <p className="text-lg font-black text-red-600">৳ {(summary?.totalDue || 0).toLocaleString()}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-teal-500 shadow-sm border">
                            <p className="text-gray-500 text-[10px] font-bold uppercase">Net Profit</p>
                            <p className="text-lg font-black text-teal-600">৳ {(summary?.netProfit || 0).toLocaleString()}</p>
                        </div>

                        <div className="bg-white p-4 rounded-lg border-l-4 border-purple-500 shadow-sm border">
                            <p className="text-gray-500 text-[10px] font-bold uppercase">Doc Payout Expense</p>
                            <p className="text-lg font-black text-purple-600">৳ {(summary?.totalDoctorPaid || 0).toLocaleString()}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-orange-500 shadow-sm border">
                            <p className="text-gray-500 text-[10px] font-bold uppercase">Agent Payout Expense</p>
                            <p className="text-lg font-black text-orange-600">৳ {(summary?.totalAgentCommission || 0).toLocaleString()}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-pink-500 shadow-sm border">
                            <p className="text-gray-500 text-[10px] font-bold uppercase">Performer Payout Expense</p>
                            <p className="text-lg font-black text-pink-600">৳ {(summary?.totalPerformerPaid || 0).toLocaleString()}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-red-500 shadow-sm border">
                            <p className="text-gray-500 text-[10px] font-bold uppercase">Others Expense</p>
                            <p className="text-lg font-black text-red-600">৳ {(summary?.totalOthersExpense || 0).toLocaleString()}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-purple-600 bg-purple-50/30 shadow-sm border">
                            <p className="text-purple-700 text-[10px] font-black uppercase">🏥 Hospital Disc Loss</p>
                            <p className="text-lg font-black text-purple-700">৳ {(summary?.discountHospital || 0).toLocaleString()}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-blue-600 bg-blue-50/30 shadow-sm border">
                            <p className="text-blue-700 text-[10px] font-black uppercase">Doctor Disc Loss</p>
                            <p className="text-lg font-black text-blue-700">৳ {(summary?.discountDoctor || 0).toLocaleString()}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg border-l-4 border-orange-600 bg-orange-50/30 shadow-sm border">
                            <p className="text-orange-700 text-[10px] font-black uppercase">Agent Disc Loss</p>
                            <p className="text-lg font-black text-orange-700">৳ {(summary?.discountAgent || 0).toLocaleString()}</p>
                        </div>
                    </div>

                    {/*  SIDE-BY-SIDE TABLES */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        
                        {/* --- INCOME TABLE --- */}
                        <div className="border rounded-lg overflow-hidden flex flex-col h-full bg-white shadow-sm">
                            <div className="bg-green-50 p-3 border-b border-green-200">
                                <h3 className="text-green-800 font-bold text-center uppercase text-sm">Details of Income</h3>
                            </div>
                            <div className="flex-grow overflow-x-auto">
                                <table className="w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="p-2 text-left text-[10px] font-bold text-gray-600 uppercase">Date</th>
                                            <th className="p-2 text-left text-[10px] font-bold text-gray-600 uppercase">Description</th>
                                            <th className="p-2 text-right text-[10px] font-bold text-gray-600 uppercase">Net</th>
                                            <th className="p-2 text-right text-[10px] font-bold text-gray-600 uppercase">Paid</th>
                                            <th className="p-2 text-right text-[10px] font-bold text-gray-600 uppercase">Due</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {loading ? (
                                            <TableSkeleton rowCount={5} columns={[{ width: "100%" }]} />
                                        ) : paginatedIncome.length === 0 ? (
                                            <tr><td colSpan="5" className="p-10 text-center text-gray-400 text-xs italic">No Income Records</td></tr>
                                        ) : (
                                            paginatedIncome.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="p-2 text-xs text-gray-700 whitespace-nowrap">{formatDateDisplay(row.date)}</td>
                                                    <td className="p-2 text-xs text-gray-700 leading-tight">{row.description}</td>
                                                    <td className="p-2 text-right text-xs font-bold text-gray-700">৳{row.net.toLocaleString()}</td>
                                                    <td className="p-2 text-right text-xs font-bold text-green-600">৳{row.paid.toLocaleString()}</td>
                                                    <td className="p-2 text-right text-xs font-bold text-red-600">৳{row.due.toLocaleString()}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {/* --- EXPENSE TABLE --- */}
                        <div className="border rounded-lg overflow-hidden flex flex-col h-full bg-white shadow-sm">
                            <div className="bg-red-50 p-3 border-b border-red-200">
                                <h3 className="text-red-800 font-bold text-center uppercase text-sm">Details of Expense</h3>
                            </div>
                            <div className="flex-grow">
                                <table className="w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="p-2 text-left text-[10px] font-bold text-gray-600 uppercase">Date</th>
                                            <th className="p-2 text-left text-[10px] font-bold text-gray-600 uppercase">Description</th>
                                            <th className="p-2 text-right text-[10px] font-bold text-gray-600 uppercase">Amount</th>
                                        </tr>
                                    </thead>
                                    {/* --- EXPENSE TABLE --- */}
                                    <tbody className="divide-y divide-gray-200">
                                        {loading ? (
                                            <TableSkeleton rowCount={5} columns={[{ width: "100%" }]} />
                                        ) : paginatedExpense.length === 0 ? (
                                            <tr><td colSpan="3" className="p-10 text-center text-gray-400 text-xs italic">No Expense Records</td></tr>
                                        ) : (
                                            paginatedExpense.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50">
                                                    <td className="p-2 text-xs text-gray-700 whitespace-nowrap">{formatDateDisplay(row.date)}</td>
                                                    <td className="p-2 text-xs text-gray-700 leading-tight">{row.description}</td>
                                                    <td className="p-2 text-right text-xs font-bold text-red-600">৳{row.amount.toLocaleString()}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    {/* --- PAGINATION --- */}
                    {totalPages > 1 && (
                        <div className="p-5 text-end print-hidden">
                            <div className="flex items-center justify-between gap-2 w-full flex-wrap">
                                <p className="text-sm text-gray-600 font-medium">
                                    Records {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, Math.max(incomeData.length, expenseData.length))} | Page {currentPage} of {totalPages}
                                </p>
                                <nav aria-label="Table navigation">
                                    <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden bg-white">
                                        <li>
                                            <button
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                className="table-pagination-item disabled:opacity-50 disabled:bg-gray-50 p-2 border-r"
                                            >
                                                <MdKeyboardArrowLeft size={20} />
                                            </button>
                                        </li>
                                        {renderPagination()}
                                        <li>
                                            <button
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                className="table-pagination-item disabled:opacity-50 disabled:bg-gray-50 p-2 border-l"
                                            >
                                                <MdKeyboardArrowRight size={20} />
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PathologyAccountSummary;